import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, getToken, onAuthChange } from './api';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(getToken());
  const [isInitializing, setIsInitializing] = useState<boolean>(!!getToken());
  const isAuthenticated = token !== null;

  useEffect(() => {
    // Subscribe to auth changes
    const unsubscribe = onAuthChange((newToken) => {
      setToken(newToken);
    });

    return unsubscribe;
  }, []);

  // Initial token validation - refresh immediately on mount if we have a token
  useEffect(() => {
    const validateToken = async () => {
      if (!getToken()) {
        setIsInitializing(false);
        return;
      }

      console.log('Validating authentication token...');
      const response = await api.refreshToken();
      if (response && response.token) {
        console.log('Token validated and refreshed successfully');
      } else {
        console.warn('Token validation failed, logging out');
        // Token is invalid, clear it
        setToken(null);
      }
      setIsInitializing(false);
    };

    validateToken();
  }, []);

  // Auto-refresh token every 2 minutes
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const refreshInterval = setInterval(async () => {
      console.log('Refreshing authentication token...');
      const response = await api.refreshToken();
      if (response && response.token) {
        console.log('Token refreshed successfully');
        // The setToken in api.ts will trigger the onAuthChange listener
      } else {
        console.warn('Failed to refresh token');
      }
    }, 2 * 60 * 1000); // 2 minutes

    return () => {
      clearInterval(refreshInterval);
    };
  }, [isAuthenticated]);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const response = await api.login(email, password);

    if (response.type === 'success') {
      return { success: true };
    } else {
      return { success: false, error: response.error };
    }
  };

  const logout = (): void => {
    api.logout();
  };

  // Show loading state while validating token on initial mount
  if (isInitializing) {
    return null; // Or a loading spinner if you prefer
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
